import React, { useEffect, useState, useCallback } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { Ticket, Plus, X } from 'lucide-react';

const REQUEST_TYPES = ['hardware_issue', 'software_issue', 'new_device_request', 'access_request', 'other'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STATUS_COLORS = {
  open: 'bg-amber-50 text-amber-700', in_progress: 'bg-blue-50 text-blue-700',
  resolved: 'bg-emerald-50 text-emerald-700', closed: 'bg-slate-100 text-slate-500',
};

/** Employee-facing IT service requests. Used as a tab on My Profile. */
export default function MyITRequests() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ request_type: 'hardware_issue', title: '', description: '', priority: 'medium' });

  const load = useCallback(() => {
    apiGet('/it/me/requests').then((r) => setRows(r.items || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      await apiPost('/it/requests', form);
      toast.success('IT request raised'); setShow(false);
      setForm({ request_type: 'hardware_issue', title: '', description: '', priority: 'medium' }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="my-it-requests">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-sm flex items-center gap-2"><Ticket size={16} /> My IT Requests</h3>
        <button onClick={() => setShow(true)} data-testid="raise-it-request-btn" className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-1.5"><Plus size={12} /> Raise Request</button>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm" data-testid="my-requests-empty">No IT requests yet.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {['Ticket', 'Title', 'Priority', 'Status', 'Raised'].map((h) => <th key={h} className="text-left px-3 py-2 font-display font-bold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`my-req-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{r.request_no}</td>
                  <td className="px-3 py-2 font-display font-bold">{r.title}</td>
                  <td className="px-3 py-2 capitalize">{r.priority}</td>
                  <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm ${STATUS_COLORS[r.status]}`}>{(r.status || '').replace(/_/g, ' ')}</span></td>
                  <td className="px-3 py-2 text-slate-400">{(r.created_at || '').slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)} data-testid="raise-request-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
            <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Raise IT Request</h3><button type="button" onClick={() => setShow(false)}><X size={18} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="lbl">Type</span><select className="inp" value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} data-testid="req-type">{REQUEST_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</select></label>
              <label className="block"><span className="lbl">Priority</span><select className="inp" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="req-priority">{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select></label>
            </div>
            <label className="block"><span className="lbl">Title *</span><input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="req-title" /></label>
            <label className="block"><span className="lbl">Description</span><textarea rows={3} className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <button type="submit" data-testid="req-submit" className="w-full py-2.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c]">Submit Request</button>
            <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}
